import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import STATE_IMAGES from '../data/state-images.json'; // s139 Phase 7b (DECISIONS #70): Commons, location confirmed by category
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AffiliateSlot from '../components/AffiliateSlot.jsx';
import { generateStateFaqs, faqJsonLd } from '../components/stateFaqs.js';
import { loadMarkdownBySlug } from '../utils/markdownLoader.js';
import Seo from '../components/Seo.jsx';
import { breadcrumbSchema } from '../utils/schema.js';
import { resolveAuthor } from '../data/contributors.js';
import { hardnessColor, hardnessFigure, hardnessLabel } from '../data/water.js';
import { stateRange, fmtRange } from '../data/hardnessBaseline.js';
import { STATE_RULES } from '../data/localRules.js';
import StateRules from '../components/StateRules.jsx';
// State data is now split per-state: a lightweight STATES_INDEX (~5 KB
// gzip) holds the slug/name/region/author + the hardness summary fields
// we need for neighbor labels and author state-counts; each full state
// record lives in src/data/states/<slug>.js and is loaded on demand
// via loadStateBySlug(). See session-35 handoff for the rationale.

// Lazy glob — each state's markdown becomes its own async chunk.
// Before Session 34 this was eager-globbed, which (post route-splitting)
// caused rollup to bundle all 50 states into a single 1.35 MB chunk
// attached to whichever lazy page first imported the glob. With lazy
// globbing, a visitor to /states/california fetches only california.md.
//
// Session 39: the glob points at `enriched/` rather than the source
// directory. `src/states/<slug>.md` remains the hand-edited source-of-
// truth; `scripts/inject-state-city-links.mjs` regenerates the enriched
// copies under `src/states/enriched/<slug>.md`, in which the first
// in-prose mention of each city with a dedicated city page has been
// wrapped in a markdown link. Hand edits go to the source files; rerun
// the script to regenerate.
const stateModules = import.meta.glob('../states/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

// Parse frontmatter from raw markdown (matches ArticlePage / GuidePage parser)
function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/);
  if (!match) return { frontmatter: {}, body: raw };

  const yamlText = match[1];
  const body = match[2];
  const frontmatter = {};

  const lines = yamlText.split('\n');
  let currentKey = null;
  let currentSubArrayKey = null;

  for (const line of lines) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;

    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      currentSubArrayKey = null;
      const value = topMatch[2].trim();

      if (value === '') {
        frontmatter[currentKey] = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        frontmatter[currentKey] = value.slice(1, -1);
      } else {
        frontmatter[currentKey] = value;
      }
      continue;
    }

    const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subMatch) {
      const subKey = subMatch[1];
      const subValue = subMatch[2].trim();
      if (frontmatter[currentKey] === null || typeof frontmatter[currentKey] !== 'object') {
        frontmatter[currentKey] = {};
      }
      if (subValue === '') {
        frontmatter[currentKey][subKey] = [];
        currentSubArrayKey = subKey;
      } else if (subValue.startsWith('"') && subValue.endsWith('"')) {
        frontmatter[currentKey][subKey] = subValue.slice(1, -1);
        currentSubArrayKey = null;
      } else {
        frontmatter[currentKey][subKey] = subValue;
        currentSubArrayKey = null;
      }
      continue;
    }

    const subArrayMatch = line.match(/^    - (.*)$/);
    if (subArrayMatch && currentSubArrayKey && Array.isArray(frontmatter[currentKey]?.[currentSubArrayKey])) {
      const item = subArrayMatch[1].trim();
      if (item.startsWith('"') && item.endsWith('"')) {
        frontmatter[currentKey][currentSubArrayKey].push(item.slice(1, -1));
      } else {
        frontmatter[currentKey][currentSubArrayKey].push(item);
      }
      continue;
    }

    const arrayMatch = line.match(/^  - (.*)$/);
    if (arrayMatch) {
      currentSubArrayKey = null;
      const item = arrayMatch[1].trim();
      if (!Array.isArray(frontmatter[currentKey])) {
        frontmatter[currentKey] = [];
      }
      if (item.startsWith('"') && item.endsWith('"')) {
        frontmatter[currentKey].push(item.slice(1, -1));
      } else {
        frontmatter[currentKey].push(item);
      }
      continue;
    }
  }

  return { frontmatter, body };
}

// Severity → color for the contaminants table
function severityColor(severity, T) {
  switch (severity) {
    case 'severe': return T.sienna;
    case 'high': return T.sienna;
    case 'moderate': return T.inkSoft;
    case 'mild': return T.inkFaint;
    default: return T.inkSoft;
  }
}

// Hardness tier → color.
//
// s72 BUGFIX. This used to switch on waterProfile.dominantTier, which the
// states.js schema block documents as an enum of five values ('soft' |
// 'moderate' | 'hard' | 'very hard' | 'extremely hard'). It is not one: 44 of
// 50 states carry free-text prose there ("very soft (Willamette Valley),
// moderately hard (central/eastern)").
//
// Two separate measured numbers, because they are different claims:
//   • 46 of 50 states fell through both string tests to the T.sienna default,
//     i.e. for 46 states the colour was not derived from the data at all.
//   • 22 of 50 render a DIFFERENT colour once computed from the numbers.
//     That is the visible impact. 15 of those 22 were UNDER-warning (they
//     should be alarm and showed caution: Texas 180–600, Iowa 200–500,
//     Ohio 95–425). 3 were OVER-warning (Massachusetts 18–220, the Carolinas).
//
// Now it reads the NUMBERS, via the canonical bands in src/data/water.js, so
// it cannot be defeated by a prose field. dominantTier is still rendered on
// this page — as the sentence it actually is, never parsed.
//
// The colour keys off the range MIDPOINT, which is a crude summary of a range
// as wide as Ohio's 95–425 and is chosen only because the cell is one colour.
// The honest signal is the range itself, which is printed right beside it.
function tierColor(_tierIgnored, T, mgL) {
  return hardnessColor(mgL, T);
}

// Convert a heading string into a stable anchor ID.
// Mirrors the convention common to docs renderers (GitHub, MDX, etc.).
function slugifyHeading(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Months for the cleaning-calendar timeline visual.
const TIMELINE_MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
// Season → 0-indexed month spans for the visual band overlay.
// Spring: Mar-May (2-4), Summer: Jun-Aug (5-7), Fall: Sep-Nov (8-10), Winter: Dec-Feb (11,0,1)
const SEASON_MONTHS = {
  spring: [2, 3, 4],
  summer: [5, 6, 7],
  fall: [8, 9, 10],
  winter: [11, 0, 1],
};

export default function StatePage() {
  const { slug } = useParams();
  const [dark, setDark] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stateData, setStateData] = useState(null);
  // Count of states bylined by this page's author — surfaced in the
  // top contributor mini-card to add credibility (e.g. "10 state pages
  // by Easton →"). Derived from STATES in the dynamic-imported chunk
  // so it doesn't reflate the main bundle.
  const [authorStateCount, setAuthorStateCount] = useState(null);
  // Search/filter state for the "jump to your city" dropdown.
  const [citySearch, setCitySearch] = useState('');
  // Cities that have dedicated /states/:stateSlug/:citySlug pages.
  // Loaded from the lightweight CITIES_INDEX in cities/index.js (which
  // carries every field this page's "cities we cover" grid actually
  // renders: cityName, citySlug, stateSlug, hardness, populationK,
  // firstZip). The full per-state cities module is not loaded here —
  // this page never needs the long fields (neighborhoodDetail, etc.).
  const [cityPagesAvail, setCityPagesAvail] = useState([]);
  // Neighboring states (land-adjacent) with stub metadata for the
  // "where to read next, by neighbor" cross-link grid. Loaded from
  // stateAdjacency.js + states.js in the dynamic-import bundle.
  const [neighboringStates, setNeighboringStates] = useState([]);

  // s45.3: cityZipBySlug map — built unconditionally near the top of the
  // component to satisfy the Rules of Hooks (hooks must run in the same
  // order on every render; if this were placed after the loading/not-found
  // early returns, the first-render-only-some-hooks vs. later-render-all-
  // hooks mismatch triggers React error #310). Depends only on
  // cityPagesAvail, so the memo recomputes only when that state updates.
  const cityZipBySlug = useMemo(() => {
    const map = {};
    for (const c of cityPagesAvail || []) {
      if (c?.citySlug && c?.firstZip) {
        map[c.citySlug] = { zip: c.firstZip, citySlug: c.citySlug };
      }
    }
    return map;
  }, [cityPagesAvail]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStateData(null);

    // Resolve the markdown body via the lazy glob loader. Only the matching
    // state's .md chunk is fetched — see utils/markdownLoader.js for the
    // filename-heuristic that hits the right file in one request.
    loadMarkdownBySlug(stateModules, slug, parseFrontmatter).then((found) => {
      if (cancelled) return;
      setContent(found);
      setCitySearch('');
      window.scrollTo(0, 0);

      // Resolve stateData by dynamically importing only this slug's
      // per-state module — the matching chunk under
      // src/data/states/<slug>.js is fetched, not the 50-state monolith.
      // The author state-count and neighbor labels then come from the
      // lightweight STATES_INDEX (separate small chunk). Pre-session-35
      // this was a single import('../data/states.js') of the 135 KB monolith.
      //
      // s45.2: setLoading(false) is deferred until the critical-path
      // stateData fetch completes. The body of this component dereferences
      // stateData.waterProfile.* without optional chaining, so rendering
      // before stateData arrives blows up. The deferral keeps the
      // "ONE MOMENT" splash visible across both fetches instead of
      // letting the render flash through with stateData=null in the
      // gap between markdown-resolves and module-resolves.
      const stateSlug = found?.frontmatter?.stateSlug;
      const authorSlug = found?.frontmatter?.author?.slug;
      if (stateSlug) {
        import('../data/states/index.js').then((indexMod) => {
          if (cancelled) return;
          // Author state count — derived from the lightweight summary,
          // which carries the assignedAuthor field for every state.
          if (authorSlug && indexMod.STATES_INDEX) {
            const count = indexMod.STATES_INDEX.filter(
              (s) => s.assignedAuthor === authorSlug
            ).length;
            setAuthorStateCount(count);
          }
          // Resolve the per-state full record AND neighbors in parallel.
          // Neighbors use only the lightweight summary, which already
          // carries name + abbreviation + hardness range + dominantTier.
          indexMod.loadStateBySlug(stateSlug).then((full) => {
            if (cancelled) return;
            setStateData(full);
            // Critical-path data is in. Drop the loading splash now,
            // even if neighbors/cities/adjacency haven't resolved yet —
            // those are non-critical (the render path guards them with
            // optional chaining and length checks).
            setLoading(false);
          });
          import('../data/stateAdjacency.js').then((adjMod) => {
            if (cancelled) return;
            const neighborSlugs = adjMod.STATE_ADJACENCY[stateSlug] || [];
            const stateByslug = {};
            for (const s of (indexMod.STATES_INDEX || [])) stateByslug[s.slug] = s;
            const neighbors = neighborSlugs
              .map((ns) => stateByslug[ns])
              .filter(Boolean)
              .map((s) => ({
                slug: s.slug,
                name: s.name,
                abbreviation: s.abbreviation,
                hardnessLow: s.hardnessLow,
                hardnessHigh: s.hardnessHigh,
                dominantTier: s.dominantTier,
              }));
            setNeighboringStates(neighbors);
          });
        }).catch(() => {
          // If the states-index module fails to load, surface the
          // "not found" state rather than hang on the splash forever.
          if (cancelled) return;
          setStateData(null);
          setLoading(false);
        });
        // Pull the cities that have dedicated pages for this state, so we
        // can render a "Cities we cover" grid linking to /states/:s/:c.
        // After session 36, this reads the lightweight CITIES_INDEX —
        // the grid only renders fields the index already carries, so no
        // per-state cities module is loaded by this page.
        import('../data/cities/index.js').then((mod) => {
          if (cancelled) return;
          setCityPagesAvail(mod.getCitySummariesByState(stateSlug));
        });
      } else {
        // No stateSlug in frontmatter — there's nothing more to load.
        // Drop the splash; the !content / !stateData guards handle the
        // "not found" path below.
        setStateData(null);
        setAuthorStateCount(null);
        setCityPagesAvail([]);
        setNeighboringStates([]);
        setLoading(false);
      }
    }).catch(() => {
      // If the markdown loader rejects (very unlikely but defensive),
      // drop the splash and show the not-found state.
      if (cancelled) return;
      setContent(null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [slug]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontMono, fontSize: 11, letterSpacing: '0.22em',
        color: T.inkFaint, padding: 40,
      }}>
        ONE MOMENT
      </div>
    );
  }

  if (!content || !stateData) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
        padding: 40,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: T.inkFaint, marginBottom: 24 }}>State page not found.</p>
          <Link to="/" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            fontStyle: 'normal',
          }}>
            ← BACK TO HOMEPAGE
          </Link>
        </div>
      </div>
    );
  }

  const { frontmatter: fm, body } = content;
  // s80: byline display fields come from contributors.js (one source).
  const author = resolveAuthor(fm.author);
  // stateData is now resolved in the useEffect above (dynamic import of states.js).
  // It may briefly be null before the dynamic chunk loads, even after `content`
  // is set — render paths below already null-check stateData so this is safe.

  // Build a citySlug → flagship ZIP map from the dedicated city-page data so
  // each topCities card can deep-link into HardWaterScorer prefilled. After
  // session 36 the source is the lightweight CITIES_INDEX, which carries
  // `firstZip` (the first entry of the original zips[] array — the only ZIP
  // any list-card surface ever rendered). topCities entries without a
  // matching cityPagesAvail record simply don't render the deep-link CTA,
  // which is the right behavior — we only promise an exact match when we
  // have one.
  //
  // s45.3: cityZipBySlug useMemo was previously declared here, AFTER the
  // early-return guards. Hooks must run in the same order on every render
  // (React Rules of Hooks) — calling fewer hooks on early-return renders
  // than on full-render renders triggers React error #310 ("Rendered more
  // hooks than during the previous render"). The useMemo has been moved
  // up next to the useState/useEffect block so it always runs.

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Match article-prose styling
  const proseStyles = `
    .state-prose p {
      font-family: ${fontDisplay};
      font-size: 18px;
      line-height: 1.7;
      color: ${T.ink};
      margin: 0 0 24px;
      font-weight: 400;
    }
    .state-prose h2 {
      font-family: ${fontDisplay};
      font-size: 32px;
      font-weight: 400;
      line-height: 1.15;
      letter-spacing: -0.02em;
      margin: 56px 0 24px;
      color: ${T.ink};
    }
    .state-prose h3 {
      font-family: ${fontDisplay};
      font-size: 22px;
      font-weight: 500;
      line-height: 1.2;
      margin: 36px 0 16px;
      color: ${T.ink};
    }
    .state-prose blockquote {
      border-left: 2px solid ${T.sienna};
      padding: 4px 0 4px 28px;
      margin: 32px 0;
      font-family: ${fontDisplay};
      font-style: italic;
      font-size: 22px;
      line-height: 1.45;
      color: ${T.ink};
      font-weight: 400;
    }
    .state-prose blockquote em { color: ${T.sienna}; }
    .state-prose strong { font-weight: 600; color: ${T.ink}; }
    .state-prose em { font-style: italic; color: ${T.sienna}; }
    .state-prose ul, .state-prose ol {
      font-family: ${fontDisplay};
      font-size: 17px;
      line-height: 1.65;
      color: ${T.ink};
      padding-left: 28px;
      margin: 0 0 24px;
    }
    .state-prose li { margin-bottom: 8px; }
    .state-prose a {
      color: ${T.sienna};
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 3px;
    }
    .state-prose hr {
      border: none;
      border-top: 1px solid ${T.rule};
      margin: 56px 0;
    }
  `;

  // s80 (debt #14): page schema goes through <Seo jsonLd>, not a hand-written
  // <script>. FAQPage (when stateData has resolved) + BreadcrumbList.
  const statePath = `/states/${fm.stateSlug || fm.slug || slug}`;
  const stateFaqJson = stateData ? faqJsonLd(generateStateFaqs(stateData)) : null;
  // s136 (DECISIONS #58): the headline hardness is what the state's covered utilities publish.
  const headRange = stateData ? stateRange(stateData.slug, stateData.waterProfile?.hardnessLow, stateData.waterProfile?.hardnessHigh) : null;
  const stateJsonLd = [
    stateFaqJson,
    breadcrumbSchema([
      { name: 'Guide', path: '/' },
      { name: 'States', path: '/states' },
      { name: stateData?.name || fm.title, path: statePath },
    ]),
  ].filter(Boolean);

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <Seo
        title={fm.metaTitle || fm.title}
        description={fm.metaDescription}
        path={statePath}
        ogType="article"
        jsonLd={stateJsonLd}
      />
      <style>{proseStyles}</style>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.15em', color: T.inkFaint,
          marginBottom: 36,
        }}>
          GUIDE / STATES / {stateData?.name?.toUpperCase() || fm.slug?.toUpperCase()}
        </div>

        {/* Region tag + read time */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 20,
        }}>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
          }}>
            STATE PAGE {stateData?.region && <>&nbsp;&nbsp;·&nbsp;&nbsp; {stateData.region.toUpperCase()}</>}
          </span>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.15em', color: T.inkFaint,
          }}>
            {fm.readTime} {fm.wordCount && `· ${fm.wordCount} WORDS`}
          </span>
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
          lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: '0 0 24px',
        }}>
          {fm.title}
        </h1>

        {STATE_IMAGES[slug] && (
          <figure data-region="state-hero" style={{ margin: '0 0 28px' }}>
            <img src={STATE_IMAGES[slug].hero} alt={STATE_IMAGES[slug].alt} width={1200} height={800} loading="eager" decoding="async"
              style={{ width: '100%', height: 'auto', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block', border: `1px solid ${T.rule}` }} />
            <figcaption style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.12em', color: T.inkFaint, marginTop: 8, textAlign: 'right' }}>
              PHOTO <a href={STATE_IMAGES[slug].creditUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: T.inkFaint, textDecoration: 'none' }}>{STATE_IMAGES[slug].credit.toUpperCase()}</a>
              {' / WIKIMEDIA COMMONS · '}{STATE_IMAGES[slug].license.toUpperCase()}
            </figcaption>
          </figure>
        )}

        {/* Author */}
        {author && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            paddingTop: 20, paddingBottom: 20,
            borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: T.surface, border: `1px solid ${T.rule}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: fontDisplay, fontSize: 20, color: T.inkSoft,
            }}>
              {author.initial || author.name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <Link
                to={`/contributors/${author.slug}`}
                style={{
                  fontFamily: fontBody, fontWeight: 500, fontSize: 14,
                  color: T.ink, textDecoration: 'none',
                }}
              >
                {author.name}
              </Link>
              <div style={{
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.12em', color: T.inkFaint,
              }}>
                {author.role}
                {authorStateCount && authorStateCount > 1 && (
                  <>
                    <span style={{ margin: '0 6px', color: T.inkFaint }}>·</span>
                    <span style={{ color: T.sienna }}>
                      {authorStateCount} STATE PAGES
                    </span>
                  </>
                )}
              </div>
            </div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.12em', color: T.inkFaint,
              textAlign: 'right',
            }}>
              {fm.updatedDate && <>UPDATED {formatDate(fm.updatedDate).toUpperCase()}<br /></>}
              {fm.publishedDate && <>PUB. {formatDate(fm.publishedDate).toUpperCase()}</>}
            </div>
          </div>
        )}

        {/* Water profile snapshot */}
        {stateData?.waterProfile && (
          <div style={{
            background: T.surface,
            borderLeft: `2px solid ${T.sienna}`,
            padding: '24px 28px', marginBottom: 40,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              WATER AT A GLANCE
            </div>
            <p style={{
              fontFamily: fontDisplay, fontSize: 19, fontWeight: 400,
              lineHeight: 1.4, margin: '0 0 18px', color: T.ink,
            }}>
              {stateData.waterProfile.summary}
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 18,
              paddingTop: 16,
              borderTop: `1px solid ${T.rule}`,
            }}>
              <div>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.15em', color: T.inkFaint,
                  marginBottom: 6,
                }}>
                  {headRange.pub ? 'PUBLISHED HARDNESS' : 'PLANNING RANGE'}
                </div>
                <div style={{
                  fontFamily: fontMono, fontSize: 16, color: headRange.pub ? T.ink : T.inkFaint,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}>
                  {fmtRange(headRange)}
                  <span style={{ fontSize: 11, color: T.inkFaint, marginLeft: 4 }}>mg/L</span>
                </div>
                <div style={{ fontFamily: fontMono, fontSize: 9, letterSpacing: '0.08em', color: T.inkFaint, marginTop: 4 }}>
                  {headRange.pub ? `${headRange.n} OF ${headRange.of} CITY UTILITIES` : 'NO COVERED UTILITY PUBLISHES'}
                </div>
              </div>
              <div>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.15em', color: T.inkFaint,
                  marginBottom: 6,
                }}>
                  {headRange.pub ? 'MEDIAN CITY' : 'DOMINANT TIER'}
                </div>
                <div style={{
                  fontFamily: fontMono, fontSize: 14,
                  color: headRange.pub ? hardnessColor(headRange.med, T) : T.inkFaint,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  {headRange.pub ? `${headRange.med} mg/L · ${hardnessLabel(headRange.med)}` : stateData.waterProfile.dominantTier}
                </div>
              </div>
              <div>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.15em', color: T.inkFaint,
                  marginBottom: 6,
                }}>
                  SOURCE
                </div>
                <div style={{
                  fontFamily: fontMono, fontSize: 14, color: T.ink,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  overflowWrap: 'anywhere', // s139: long utility names (Colorado) widened phones to 422px
                }}>
                  {stateData.waterProfile.dominantSource}
                </div>
              </div>
            </div>

            {/* s72 — reciprocal link into the Water Atlas leaf for this state.
                The two pages answer different questions (see WaterStatePage.jsx
                header): this page is "how do you clean here", /water/<slug> is
                "how hard is the water here, in which city, and what does that
                change". Each is one click from the other. */}
            <div style={{
              marginTop: 18, paddingTop: 14,
              borderTop: `1px solid ${T.rule}`,
            }}>
              <Link
                to={`/water/${stateData.slug}`}
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.12em', color: T.sienna,
                  textDecoration: 'none',
                }}
              >
                EVERY {stateData.name.toUpperCase()} CITY READING, IN THE WATER ATLAS →
              </Link>
              {/* s136 (Phase 6, tools pre-filled; DECISIONS #61): the planners open set to this state,
                  where the reader picks a city. Not on city pages: a per-city row repeated on all 271
                  raised template share (346 → 347 shared, 9.44% → 9.6%) and the share gate refused it. */}
              <div data-region="state-planners" style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.12em', color: T.inkFaint, marginTop: 10 }}>
                PLAN A JOB IN {stateData.name.toUpperCase()}:{' '}
                <Link to={`/tools/regional-protocol-generator?state=${stateData.slug}`} style={{ color: T.sienna, textDecoration: 'none' }}>PROTOCOL</Link>
                {' · '}
                <Link to={`/tools/cleaning-schedule-builder?state=${stateData.slug}`} style={{ color: T.sienna, textDecoration: 'none' }}>SCHEDULE</Link>
              </div>
            </div>
          </div>
        )}

        {/* Table of contents from frontmatter sections */}
        {Array.isArray(fm.sections) && fm.sections.length > 0 && (
          <div style={{
            marginBottom: 48,
            paddingBottom: 32,
            borderBottom: `1px solid ${T.rule}`,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 16,
            }}>
              IN THIS PAGE
            </div>
            <ol style={{
              fontFamily: fontDisplay, fontSize: 16,
              color: T.ink, lineHeight: 1.6,
              paddingLeft: 24, margin: 0,
            }}>
              {fm.sections.map((section, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  <a
                    href={`#${slugifyHeading(section)}`}
                    style={{
                      color: T.ink, textDecoration: 'none',
                      borderBottom: `1px solid transparent`,
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = T.sienna}
                    onMouseLeave={(e) => e.currentTarget.style.borderBottomColor = 'transparent'}
                  >
                    {section}
                  </a>
                </li>
              ))}
            </ol>

            {/* Jump to city — appears when topCities data is available */}
            {stateData?.topCities && stateData.topCities.length > 0 && (
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${T.rule}` }}>
                <div style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.inkFaint,
                  marginBottom: 12,
                }}>
                  JUMP TO YOUR CITY
                </div>
                <select
                  value={citySearch}
                  onChange={(e) => {
                    const target = e.target.value;
                    setCitySearch(target);
                    if (target) {
                      const el = document.getElementById(`city-${slugifyHeading(target)}`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    fontFamily: fontBody, fontSize: 15,
                    padding: '10px 12px',
                    background: T.surface, color: T.ink,
                    border: `1px solid ${T.rule}`,
                    borderRadius: 2,
                  }}
                >
                  <option value="">— Select a city —</option>
                  {stateData.topCities.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Hero affiliate slot — placed after TOC, before body. Reserves
            the prime above-the-fold-of-the-body real estate for the
            partner banner. Currently placeholder (links to /find-a-cleaner). */}
        {stateData?.name && (
          <AffiliateSlot
            variant="state"
            location={stateData.name}
            theme={T}
            fontDisplay={fontDisplay}
            fontBody={fontBody}
            fontMono={fontMono}
          />
        )}

        {/* Body (markdown) — H2/H3 IDs injected for TOC anchor support */}
        <div className="state-prose">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ node, children, ...props }) => {
                const text = Array.isArray(children)
                  ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
                  : (typeof children === 'string' ? children : '');
                return <h2 id={slugifyHeading(text)} {...props}>{children}</h2>;
              },
              h3: ({ node, children, ...props }) => {
                const text = Array.isArray(children)
                  ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
                  : (typeof children === 'string' ? children : '');
                return <h3 id={slugifyHeading(text)} {...props}>{children}</h3>;
              },
            }}
          >
            {body}
          </ReactMarkdown>
        </div>

        {/* Structured data: top cities */}
        {stateData?.topCities && stateData.topCities.length > 0 && (
          <div style={{ marginTop: 64 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              CITY-BY-CITY WATER PROFILE
            </div>
            <h2
              id={slugifyHeading('the big cities, in numbers')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              The big cities, in numbers
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}>
              {stateData.topCities.map((city) => (
                <div
                  key={city.name}
                  id={`city-${slugifyHeading(city.name)}`}
                  style={{
                    border: `1px solid ${T.rule}`,
                    borderTop: `2px solid ${T.sienna}`,
                    padding: '20px 22px',
                    background: T.bg,
                    scrollMarginTop: 24,
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', marginBottom: 8, gap: 12,
                    flexWrap: 'wrap',
                  }}>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 20,
                      fontWeight: 500, color: T.ink,
                    }}>
                      {city.name}
                    </div>
                    <div style={{
                      fontFamily: fontMono, fontSize: 11,
                      color: T.inkFaint, letterSpacing: '0.05em',
                    }}>
                      pop. {city.populationK}k
                    </div>
                  </div>

                  {/* Metric strip — hardness, source, utility */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                    padding: '10px 0',
                    borderTop: `1px solid ${T.rule}`,
                    borderBottom: `1px solid ${T.rule}`,
                    marginBottom: 12,
                  }}>
                    <div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 9,
                        letterSpacing: '0.15em', color: T.inkFaint,
                        marginBottom: 3,
                      }}>
                        HARDNESS
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 13, fontWeight: 500,
                        color: T.sienna, letterSpacing: '0.02em',
                      }}>
                        {hardnessFigure(city, { withUnit: true })}
                      </div>
                    </div>
                    <div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 9,
                        letterSpacing: '0.15em', color: T.inkFaint,
                        marginBottom: 3,
                      }}>
                        SOURCE
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 13, fontWeight: 500,
                        color: T.ink, letterSpacing: '0.02em',
                        textTransform: 'capitalize',
                      }}>
                        {city.sourceType}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.06em', color: T.inkFaint,
                    marginBottom: 10, textTransform: 'uppercase',
                  }}>
                    {city.utility}
                  </div>

                  <p style={{
                    fontFamily: fontBody, fontSize: 14,
                    lineHeight: 1.55, color: T.ink,
                    margin: '0 0 12px',
                  }}>
                    {city.notes}
                  </p>

                  {city.neighborhoods && city.neighborhoods.length > 0 && (
                    <div style={{
                      fontFamily: fontMono, fontSize: 10,
                      color: T.inkSoft, letterSpacing: '0.03em',
                      lineHeight: 1.6, marginBottom: 4,
                    }}>
                      <span style={{ color: T.inkFaint }}>NEIGHBORHOODS:</span> {city.neighborhoods.join(' · ')}
                    </div>
                  )}

                  {/* ZIP3-aware deep-link into the Hard Water Scorer tool.
                      Only renders when the city has a dedicated page in
                      the per-state cities module (so we have a verified
                      flagship ZIP); the tool consumes the ?zip= query
                      param on mount and auto-runs. The link uses the
                      city's firstZip from CITIES_INDEX, which is the
                      first entry of the original zips[] array in the
                      per-state cities module — chosen to be the
                      recognizable city-center ZIP, so the result lands
                      on the actual local utility number rather than a
                      regional fallback. The "Cities we cover" grid below
                      is the canonical destination for the city page;
                      this CTA is the one-tap shortcut to the tool. */}
                  {cityZipBySlug[slugifyHeading(city.name)] && (
                    <div style={{ marginTop: 12, marginBottom: 4 }}>
                      <Link
                        to={`/tools/hard-water-scorer?zip=${cityZipBySlug[slugifyHeading(city.name)].zip}`}
                        aria-label={`Score water hardness for ${city.name} (ZIP ${cityZipBySlug[slugifyHeading(city.name)].zip})`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          fontFamily: fontMono,
                          fontSize: 10,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: T.sienna,
                          textDecoration: 'none',
                          borderTop: `1px solid ${T.rule}`,
                          paddingTop: 10,
                          width: '100%',
                        }}
                      >
                        <span>Score this ZIP ({cityZipBySlug[slugifyHeading(city.name)].zip})</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                  )}

                  {/* Per-city affiliate slot — reserves prime per-city
                      real estate for the partner banner, prefilled with
                      "City, State" as the location string so when the
                      flip-the-switch session arrives, deep links work. */}
                  <AffiliateSlot
                    variant="inline"
                    location={`${city.name}, ${stateData.name}`}
                    theme={T}
                    fontDisplay={fontDisplay}
                    fontBody={fontBody}
                    fontMono={fontMono}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cities we cover — dedicated city pages within this state.
            Only renders when CITIES_INDEX has entries for the current
            state. Adds an internal-link surface from the state page
            into the city tier, and gives the state page a destination
            block for people who came in on a city-name search. */}
        {cityPagesAvail && cityPagesAvail.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              CITIES WE COVER
            </div>
            <h2
              id={slugifyHeading('cities we cover')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 8px', color: T.ink,
              }}
            >
              Dedicated city pages in {stateData?.name || 'this state'}
            </h2>
            <p style={{
              fontFamily: fontBody, fontSize: 14,
              lineHeight: 1.55, color: T.inkSoft,
              margin: '0 0 22px',
            }}>
              Each city page carries its own water profile, neighborhood
              breakdown, cost range, and city-specific operating notes.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
            }}>
              {cityPagesAvail.map((c) => {
                // c is a lightweight CITIES_INDEX entry; firstZip is the
                // only ZIP surface this card renders.
                const cZip = c.firstZip || null;
                return (
                  <div
                    key={c.citySlug}
                    style={{
                      border: `1px solid ${T.rule}`,
                      background: T.bg,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Link
                      to={`/states/${c.stateSlug}/${c.citySlug}`}
                      style={{
                        padding: '14px 16px 12px',
                        textDecoration: 'none',
                        color: T.ink,
                        display: 'block',
                      }}
                    >
                      <div style={{
                        fontFamily: fontDisplay, fontSize: 16,
                        fontWeight: 500, color: T.ink, marginBottom: 4,
                      }}>
                        {c.cityName}
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        color: T.inkFaint, letterSpacing: '0.05em',
                      }}>
                        {hardnessFigure(c, { withUnit: true })} · pop. {c.populationK}k
                      </div>
                    </Link>
                    {/* ZIP3 deep-link into HardWaterScorer for each
                        covered city. Sibling anchor (not nested) so both
                        surfaces stay valid HTML and accessible. */}
                    {cZip && (
                      <Link
                        to={`/tools/hard-water-scorer?zip=${cZip}`}
                        aria-label={`Score water hardness for ${c.cityName} (ZIP ${cZip})`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 16px 10px',
                          borderTop: `1px solid ${T.rule}`,
                          fontFamily: fontMono, fontSize: 9,
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: T.sienna,
                          textDecoration: 'none',
                        }}
                      >
                        <span>Score {cZip}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Structured data: regional contaminants */}
        {/* s112 Phase 4b: state-level rules (working at height) */}
        <StateRules rules={STATE_RULES[fm?.stateSlug || fm?.slug || slug]} T={T} fontDisplay={fontDisplay} fontMono={fontMono} />

        {stateData?.regionalContaminants && stateData.regionalContaminants.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              REGIONAL CONTAMINANTS
            </div>
            <h2
              id={slugifyHeading('what lands on the glass')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              What lands on the glass
            </h2>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: fontBody, fontSize: 14,
            }}>
              <thead>
                <tr>
                  <th style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.inkFaint,
                    textAlign: 'left', padding: '12px 12px 12px 0',
                    borderBottom: `1px solid ${T.ink}`, fontWeight: 500,
                  }}>
                    CONTAMINANT
                  </th>
                  <th style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.inkFaint,
                    textAlign: 'left', padding: '12px 12px 12px 0',
                    borderBottom: `1px solid ${T.ink}`, fontWeight: 500,
                  }}>
                    SEASON
                  </th>
                  <th style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.inkFaint,
                    textAlign: 'left', padding: '12px 0',
                    borderBottom: `1px solid ${T.ink}`, fontWeight: 500,
                  }}>
                    SEVERITY
                  </th>
                </tr>
              </thead>
              <tbody>
                {stateData.regionalContaminants.map((c, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td style={{
                        padding: '14px 12px 6px 0',
                        color: T.ink, lineHeight: 1.5,
                        fontWeight: 500,
                      }}>
                        {c.name}
                      </td>
                      <td style={{
                        padding: '14px 12px 6px 0',
                        color: T.inkSoft, lineHeight: 1.5,
                        fontFamily: fontMono, fontSize: 12,
                      }}>
                        {c.season}
                      </td>
                      <td style={{
                        padding: '14px 0 6px 0',
                        color: severityColor(c.severity, T),
                        lineHeight: 1.5,
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}>
                        {c.severity}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} style={{
                        padding: '0 0 14px 0',
                        borderBottom: i < stateData.regionalContaminants.length - 1 ? `1px solid ${T.rule}` : 'none',
                        color: T.inkSoft, lineHeight: 1.55,
                        fontSize: 13,
                      }}>
                        {c.notes}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Structured data: cleaning calendar */}
        {stateData?.cleaningCalendar && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              THE CLEANING CALENDAR
            </div>
            <h2
              id={slugifyHeading('the year, in seasons')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              The year, in seasons
            </h2>

            {/* 12-month timeline strip — visual at-a-glance of when the
                seasonal work concentrates. Each month cell is rendered
                with a band color tied to the dominant season. Purely
                decorative reinforcement of the seasonal cards below. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 2,
              marginBottom: 24,
              border: `1px solid ${T.rule}`,
              padding: 2,
            }}>
              {TIMELINE_MONTHS.map((m, i) => {
                let season = 'winter';
                if (SEASON_MONTHS.spring.includes(i)) season = 'spring';
                else if (SEASON_MONTHS.summer.includes(i)) season = 'summer';
                else if (SEASON_MONTHS.fall.includes(i)) season = 'fall';
                const bg = {
                  spring: T.sage,
                  summer: T.sienna,
                  fall: T.inkSoft,
                  winter: T.surface,
                }[season];
                const fg = season === 'winter' ? T.ink : T.bg;
                return (
                  <div
                    key={i}
                    title={season.charAt(0).toUpperCase() + season.slice(1)}
                    style={{
                      background: bg,
                      color: fg,
                      padding: '10px 0',
                      textAlign: 'center',
                      fontFamily: fontMono,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }}
                  >
                    {m}
                  </div>
                );
              })}
            </div>
            <div style={{
              display: 'flex', gap: 16, flexWrap: 'wrap',
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.12em', color: T.inkFaint,
              marginBottom: 24,
            }}>
              <span><span style={{
                display: 'inline-block', width: 10, height: 10,
                background: T.sage, marginRight: 6, verticalAlign: 'middle',
              }} />SPRING</span>
              <span><span style={{
                display: 'inline-block', width: 10, height: 10,
                background: T.sienna, marginRight: 6, verticalAlign: 'middle',
              }} />SUMMER</span>
              <span><span style={{
                display: 'inline-block', width: 10, height: 10,
                background: T.inkSoft, marginRight: 6, verticalAlign: 'middle',
              }} />FALL</span>
              <span><span style={{
                display: 'inline-block', width: 10, height: 10,
                background: T.surface, border: `1px solid ${T.rule}`,
                marginRight: 6, verticalAlign: 'middle',
              }} />WINTER</span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
            }}>
              {['spring', 'summer', 'fall', 'winter'].map((season) => (
                <div
                  key={season}
                  style={{
                    background: T.surface,
                    padding: '18px 20px',
                    borderTop: `2px solid ${T.sienna}`,
                  }}
                >
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 10,
                  }}>
                    {season.toUpperCase()}
                  </div>
                  <p style={{
                    fontFamily: fontBody, fontSize: 13,
                    lineHeight: 1.55, color: T.ink, margin: 0,
                  }}>
                    {stateData.cleaningCalendar[season]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related articles */}
        {stateData?.linkArticles && stateData.linkArticles.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              WHERE TO READ NEXT
            </div>
            <h2
              id={slugifyHeading('where to read next')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              The articles that matter most for {stateData.name}
            </h2>
            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
            }}>
              {stateData.linkArticles.map((slug) => (
                <li
                  key={slug}
                  style={{
                    borderBottom: `1px solid ${T.rule}`,
                    padding: '16px 0',
                  }}
                >
                  <Link
                    to={`/articles/${slug}`}
                    style={{
                      fontFamily: fontDisplay, fontSize: 18,
                      color: T.ink, textDecoration: 'none',
                      lineHeight: 1.4,
                    }}
                  >
                    {slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    <span style={{
                      fontFamily: fontMono, fontSize: 11,
                      color: T.sienna, marginLeft: 12,
                      letterSpacing: '0.15em',
                    }}>
                      READ →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Neighboring states — land-adjacent states for cross-state SEO
            depth. A reader on the Pennsylvania page should see Ohio, New
            York, New Jersey, Maryland, Delaware, West Virginia as the
            natural next stops. We show the dominant hardness tier on each
            card so the comparison is informative, not just navigational. */}
        {neighboringStates && neighboringStates.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              NEIGHBORING STATES
            </div>
            <h2
              id={slugifyHeading('neighboring states')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 8px', color: T.ink,
              }}
            >
              Border states with their own guides
            </h2>
            <p style={{
              fontFamily: fontBody, fontSize: 14,
              lineHeight: 1.55, color: T.inkSoft,
              margin: '0 0 22px',
              maxWidth: 620,
            }}>
              Land-adjacent states each get their own water-and-window
              profile. If you're working a regional route or moving across
              the border, these are the natural next reads.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 10,
            }}>
              {neighboringStates.map((ns) => (
                <Link
                  key={ns.slug}
                  to={`/states/${ns.slug}`}
                  style={{
                    border: `1px solid ${T.rule}`,
                    padding: '14px 16px',
                    textDecoration: 'none',
                    color: T.ink,
                    background: T.bg,
                    display: 'block',
                  }}
                >
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 17,
                    fontWeight: 500, color: T.ink, marginBottom: 4,
                  }}>
                    {ns.name}
                  </div>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    color: T.inkFaint, letterSpacing: '0.05em',
                  }}>
                    {(() => { const r = stateRange(ns.slug, ns.hardnessLow, ns.hardnessHigh); return r.lo == null ? 'WATER PROFILE' : r.pub ? `${fmtRange(r)} mg/L published · median ${hardnessLabel(r.med, { lower: true })}` : `${fmtRange(r)} mg/L planning`; })()}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* FAQ — derived from stateData. Plain reader-facing accordion
            plus a JSON-LD payload so the FAQs are eligible for Google's
            rich-result preview. */}
        {(() => {
          if (!stateData) return null;
          const faqs = generateStateFaqs(stateData);
          if (!faqs.length) return null;
          return (
            <div style={{ marginTop: 56 }}>
              <div style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                marginBottom: 14,
              }}>
                FREQUENTLY ASKED
              </div>
              <h2
                id={slugifyHeading('frequently asked')}
                style={{
                  fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                  lineHeight: 1.15, letterSpacing: '-0.02em',
                  margin: '0 0 24px', color: T.ink,
                }}
              >
                Common questions about window cleaning in {stateData.name}
              </h2>
              <div>
                {faqs.map((f, i) => (
                  <details
                    key={i}
                    style={{
                      borderBottom: `1px solid ${T.rule}`,
                      padding: '16px 0',
                    }}
                  >
                    <summary style={{
                      fontFamily: fontDisplay, fontSize: 17,
                      fontWeight: 500, color: T.ink, cursor: 'pointer',
                      lineHeight: 1.4, listStyle: 'none',
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: 16,
                    }}>
                      <span>{f.q}</span>
                      <span style={{
                        fontFamily: fontMono, fontSize: 11,
                        color: T.sienna, letterSpacing: '0.1em',
                        flexShrink: 0, marginTop: 4,
                      }}>+</span>
                    </summary>
                    <p style={{
                      fontFamily: fontBody, fontSize: 15,
                      lineHeight: 1.65, color: T.inkSoft,
                      margin: '12px 0 0',
                    }}>
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Author bio at end */}
        {author?.bio && (
          <div style={{
            marginTop: 56, paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 12,
            }}>
              ABOUT THE AUTHOR
            </div>
            <h3 style={{
              fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
              margin: '0 0 12px',
            }}>
              {author.name}
            </h3>
            {author.role && (
              <div style={{
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.15em', color: T.inkFaint,
                marginBottom: 14, textTransform: 'uppercase',
              }}>
                {author.role}
                {authorStateCount && authorStateCount > 1 && (
                  <span style={{ color: T.sienna, marginLeft: 10 }}>
                    · {authorStateCount} STATE PAGES
                  </span>
                )}
              </div>
            )}
            <p style={{
              fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
              color: T.inkSoft, margin: '0 0 12px',
            }}>
              {author.bio}
            </p>
            {author.slug && (
              <Link
                to={`/contributors/${author.slug}`}
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  color: T.sienna, textDecoration: 'none',
                  letterSpacing: '0.15em',
                }}
              >
                READ MORE BY {author.name?.toUpperCase()} →
              </Link>
            )}
          </div>
        )}

        {/* Footer */}
        <footer style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
        }}>
          <Link to="/" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO WINDOW WASHING GUIDE
          </Link>
        </footer>
      </div>
    </div>
  );
}
